import { Box, useColorModeValue } from "@chakra-ui/react";
import { Children, cloneElement, isValidElement, memo, useMemo } from "react";
import { RTableContext } from "./RTableContext";

/**
 * @typedef {object} RTableColumn
 * @property {import("react").ReactNode | string} title
 * @property {string} [size]
 * @property {"right"} [textAlign]
 */

const RTable = memo(
  /**
   * @typedef {Object} Props
   * @property {RTableColumn[]} [columns]
   * @property {string} [responsiveBreakpoint]
   * @property {boolean} [isStriped]
   * @property {import("react").ReactNode} children
   *
   * @param {Props} props
   */
  function ResponsiveTable({
    columns = [],
    responsiveBreakpoint = "425px",
    isStriped = false,
    children,
  }) {
    const value = useMemo(() => {
      return {
        columns,
        responsiveBreakpoint,
        isStriped,
      };
    }, [columns, isStriped, responsiveBreakpoint]);

    const color = useColorModeValue("gray.700", "gray.300");

    return (
      <RTableContext.Provider value={value}>
        <Box sx={{ containerType: "inline-size" }}>
          <Box
            display="table"
            w="full"
            sx={{
              [`@container (max-width: ${responsiveBreakpoint})`]: {
                "&": {
                  display: "block",
                },
              },
            }}>
            <Box
              display="table-header-group"
              sx={{
                [`@container (max-width: ${responsiveBreakpoint})`]: {
                  "&": {
                    display: "block",
                  },
                },
              }}>
              <Box
                display="table-row"
                sx={{
                  [`@container (max-width: ${responsiveBreakpoint})`]: {
                    "&": {
                      display: "none",
                    },
                  },
                }}>
                {columns.map((column, index) => (
                  <Box
                    key={index}
                    display="table-cell"
                    color={color}
                    fontWeight="bold"
                    px=".5rem"
                    pt={isStriped ? ".5rem" : undefined}
                    pb={isStriped ? ".5rem" : undefined}
                    w={column.size}
                    textAlign={column.textAlign}
                    sx={{
                      "&:first-of-type": {
                        pl: isStriped ? "1rem" : undefined,
                      },
                      "&:last-of-type": {
                        pr: isStriped ? "1rem" : undefined,
                      },
                      [`@container (max-width: ${responsiveBreakpoint})`]: {
                        "&": {
                          width: "auto",
                        },
                      },
                    }}
                    _first={{
                      pl: 0,
                    }}
                    _last={{
                      pr: 0,
                    }}>
                    {column.title}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              display="table-row-group"
              sx={{
                [`@container (max-width: ${responsiveBreakpoint})`]: {
                  "&": {
                    display: "block",
                  },
                },
              }}>
              {Children.toArray(children).map(
                (child, index) =>
                  isValidElement(child) &&
                  cloneElement(child, {
                    _isFirstRow: index === 0,
                    _isLastRow: index === Children.count(children) - 1,
                  }),
              )}
            </Box>
          </Box>
        </Box>
      </RTableContext.Provider>
    );
  },
);

export default RTable;
